(() => {
  'use strict';

  class PESBundleBuilder {
    constructor(root) {
      this.root = root;
      this.currency = root.dataset.currency || 'USD';
      this.bundleTitle = root.dataset.bundleTitle || 'PES bundle';
      this.items = Array.from(root.querySelectorAll('[data-bundle-item]'));
      this.button = root.querySelector('[data-bundle-add]');
      this.buttonText = root.querySelector('[data-bundle-button-text]');
      this.countElement = root.querySelector('[data-bundle-count]');
      this.totalElement = root.querySelector('[data-bundle-total]');
      this.messageElement = root.querySelector('[data-bundle-message]');
      this.defaultButtonLabel = this.button?.dataset.label || 'Add Selected Bundle';

      if (!this.button || this.items.length === 0) return;

      this.bindEvents();
      this.update();
    }

    bindEvents() {
      this.root.addEventListener('change', (event) => {
        if (
          event.target.matches('[data-bundle-checkbox]') ||
          event.target.matches('[data-bundle-variant]')
        ) {
          this.clearMessage();
          this.update();
        }
      });

      this.root.addEventListener('click', (event) => {
        const toggleArea = event.target.closest('[data-bundle-toggle]');
        if (!toggleArea || !this.root.contains(toggleArea)) return;

        const item = toggleArea.closest('[data-bundle-item]');
        const checkbox = item?.querySelector('[data-bundle-checkbox]');

        if (!checkbox || checkbox.disabled || item.dataset.required === 'true') return;

        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      });

      this.button.addEventListener('click', () => this.addBundle());
    }

    getVariantControl(item) {
      return item.querySelector('[data-bundle-variant]');
    }

    getVariantState(item) {
      const control = this.getVariantControl(item);

      if (!control) {
        return { id: null, price: 0, available: false, image: '' };
      }

      const source = control.tagName === 'SELECT'
        ? control.options[control.selectedIndex]
        : control;

      return {
        id: Number(control.value),
        price: Number(source?.dataset.price || 0),
        available: source?.dataset.available === 'true',
        image: source?.dataset.image || ''
      };
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: this.currency
        }).format(cents / 100);
      } catch (error) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    updateItem(item) {
      const checkbox = item.querySelector('[data-bundle-checkbox]');
      const priceElement = item.querySelector('[data-bundle-item-price]');
      const imageElement = item.querySelector('[data-bundle-image]');
      const variant = this.getVariantState(item);
      const required = item.dataset.required === 'true';

      if (priceElement) {
        priceElement.textContent = this.formatMoney(variant.price);
      }

      if (imageElement && variant.image && imageElement.tagName === 'IMG') {
        if (imageElement.src !== variant.image) {
          imageElement.src = variant.image;
          imageElement.removeAttribute('srcset');
        }
      }

      if (checkbox && !required) {
        checkbox.disabled = !variant.available;

        if (!variant.available) {
          checkbox.checked = false;
        }
      }

      return variant;
    }

    collectSelection() {
      const itemMap = new Map();
      let total = 0;
      let selectedCount = 0;
      let invalidRequiredItem = false;

      this.items.forEach((item) => {
        const checkbox = item.querySelector('[data-bundle-checkbox]');
        const required = item.dataset.required === 'true';
        const variant = this.updateItem(item);
        const selected = required || Boolean(checkbox?.checked);

        item.classList.toggle('is-selected', selected);

        if (required && !variant.available) {
          invalidRequiredItem = true;
        }

        if (!selected || !variant.available || !variant.id) return;

        total += variant.price;
        selectedCount += 1;

        if (itemMap.has(variant.id)) {
          itemMap.get(variant.id).quantity += 1;
        } else {
          itemMap.set(variant.id, {
            id: variant.id,
            quantity: 1
          });
        }
      });

      return {
        items: Array.from(itemMap.values()),
        total,
        selectedCount,
        invalidRequiredItem
      };
    }

    update() {
      const selection = this.collectSelection();

      if (this.totalElement) {
        this.totalElement.textContent = this.formatMoney(selection.total);
      }

      if (this.countElement) {
        this.countElement.textContent = `${selection.selectedCount} ${
          selection.selectedCount === 1 ? 'item' : 'items'
        } selected`;
      }

      if (this.buttonText) {
        this.buttonText.textContent = `${this.defaultButtonLabel} • ${this.formatMoney(selection.total)}`;
      }

      this.button.disabled =
        selection.invalidRequiredItem ||
        selection.items.length === 0 ||
        this.button.classList.contains('is-loading');
    }

    setLoading(isLoading) {
      this.button.classList.toggle('is-loading', isLoading);
      this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');

      if (this.buttonText) {
        this.buttonText.textContent = isLoading
          ? 'Adding bundle…'
          : this.defaultButtonLabel;
      }

      if (!isLoading) {
        this.update();
      } else {
        this.button.disabled = true;
      }
    }

    showMessage(message, type = '') {
      if (!this.messageElement) return;

      this.messageElement.textContent = message;
      this.messageElement.classList.toggle('is-error', type === 'error');
      this.messageElement.classList.toggle('is-success', type === 'success');
    }

    clearMessage() {
      this.showMessage('');
    }

    getSectionsToRender(cartDrawer) {
      const ids = new Set(['cart-drawer', 'cart-icon-bubble']);

      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        cartDrawer.getSectionsToRender().forEach((section) => {
          if (section?.id) ids.add(section.id);
        });
      }

      return Array.from(ids).slice(0, 5);
    }

    fallbackRenderSections(sections) {
      if (!sections) return;

      Object.entries(sections).forEach(([sectionId, html]) => {
        if (!html) return;

        const currentSection = document.getElementById(`shopify-section-${sectionId}`);
        if (!currentSection) return;

        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const incomingSection =
          parsedDocument.getElementById(`shopify-section-${sectionId}`) ||
          parsedDocument.body.firstElementChild;

        if (incomingSection) {
          currentSection.innerHTML = incomingSection.innerHTML;
        }
      });
    }

    openCartDrawer(cartDrawer) {
      window.setTimeout(() => {
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open(this.button);
          return;
        }

        const cartTrigger = document.querySelector(
          '#cart-icon-bubble, a.header__icon--cart, button.header__icon--cart'
        );

        if (cartTrigger) cartTrigger.click();
      }, 80);
    }

    async addBundle() {
      const selection = this.collectSelection();

      if (selection.invalidRequiredItem) {
        this.showMessage('A required product is currently unavailable.', 'error');
        return;
      }

      if (selection.items.length === 0) {
        this.showMessage('Select at least one available product.', 'error');
        return;
      }

      this.clearMessage();
      this.setLoading(true);

      const cartDrawer = document.querySelector('cart-drawer');
      const root = window.Shopify?.routes?.root || '/';
      const sections = this.getSectionsToRender(cartDrawer);

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: selection.items,
            sections,
            sections_url: window.location.pathname
          })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.description ||
            result.message ||
            'The bundle could not be added. Please try again.'
          );
        }

        let renderedWithDrawer = false;

        if (result.sections && cartDrawer && typeof cartDrawer.renderContents === 'function') {
          try {
            if (!result.id) result.id = selection.items[0].id;
            cartDrawer.renderContents(result);
            renderedWithDrawer = true;
          } catch (renderError) {
            renderedWithDrawer = false;
          }
        }

        if (!renderedWithDrawer) {
          this.fallbackRenderSections(result.sections);
          this.openCartDrawer(cartDrawer);
        }

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: {
              source: 'pes-bundle-builder',
              bundleTitle: this.bundleTitle,
              items: selection.items
            }
          })
        );

        this.showMessage('Bundle added to your cart.', 'success');
      } catch (error) {
        this.showMessage(
          error?.message || 'The bundle could not be added. Please try again.',
          'error'
        );
      } finally {
        this.setLoading(false);
      }
    }
  }

  const initializeBundles = (container = document) => {
    container.querySelectorAll('[data-pes-bundle]').forEach((root) => {
      if (root.dataset.pesBundleInitialized === 'true') return;

      root.dataset.pesBundleInitialized = 'true';
      new PESBundleBuilder(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeBundles());
  } else {
    initializeBundles();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeBundles(event.target);
  });
})();
